import {
  useState,
  useRef,
  useCallback,
  type DragEvent,
  type ChangeEvent,
} from 'react';
import { parseJsonFile } from '../../../utils/fileHandler';
import type { JsonFileInputProps } from './types';

export const JsonFileInput = ({
  label,
  placeholder,
  onFileLoaded,
  onError,
  loadedInfo,
}: JsonFileInputProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    async (file: File) => {
      try {
        const data = await parseJsonFile(file);
        onFileLoaded(data, file.name);
      } catch (e) {
        onError((e as Error).message);
      }
    },
    [onFileLoaded, onError],
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) {
        if (inputRef.current) {
          inputRef.current.files = e.dataTransfer.files;
        }
        handleFile(file);
      }
    },
    [handleFile],
  );

  const isLoaded = !!loadedInfo;

  return (
    <div className="flex-1 min-w-[180px]">
      <label className="block text-[12px] text-[#8b949e] mb-1">{label}</label>
      <div
        className={`border-2 border-dashed rounded-lg p-5 text-center cursor-pointer relative min-h-[70px] flex items-center justify-center flex-col transition-colors ${
          isDragOver
            ? 'border-[#58a6ff] bg-[#161b22]'
            : isLoaded
              ? 'border-[#238636] bg-[#161b22]'
              : 'border-[#30363d] hover:border-[#58a6ff]'
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".json"
          className="hidden"
          onChange={handleChange}
        />
        {isLoaded ? (
          <div>
            <span className="text-[#3fb950] font-semibold text-sm">
              {loadedInfo.fileName}
            </span>
            <div className="text-[11px] text-[#8b949e] mt-1">
              {loadedInfo.stats}
            </div>
          </div>
        ) : (
          <div className="text-[13px] text-[#8b949e]">{placeholder}</div>
        )}
      </div>
    </div>
  );
};
